import { studyQueryKeys } from '@/config/queryKeys';
import { useDateFormatter } from '@/hooks';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import { searchStudySamples } from '@/services/samples';
import { PaginatedSamplesResponse, SampleSearchRequest, StudySample } from '@/types';
import { flushColumnState } from '@/utils/flushColumnState';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { ColDef, GridOptions } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useSampleTableGrid } from '../../Studies/components/useSampleTableGrid';
import { DATE_FILTER_PARAMS, TEXT_FILTER_PARAMS } from '../../Studies/utils/sharedColumnDefs';
import styles from '../styles.module.css';

interface StudySamplesTableProps {
  orgId: string;
  studyId: string;
  totalSamples: number;
  passedQc: number;
}

const paginationPageSizeKey = (studyId: string) => `study-${studyId}-studySamples-pageSize`;
const VALID_SORT_FIELDS = ['name', 'instrument_run_name', 'created_at', 'updated_at', 'well_position', 'demux_index'];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 1000];
const DEFAULT_PAGE_SIZE = 25;

const gridOptions: GridOptions = {
  cacheBlockSize: 100,
  maxBlocksInCache: 10,
  suppressMovableColumns: false,
  suppressDragLeaveHidesColumns: false,
};

export const StudySamplesTable = function StudySamplesTable({
  orgId,
  passedQc,
  studyId,
  totalSamples,
}: StudySamplesTableProps) {
  const { t } = useTranslation('studies');
  const formatDate = useDateFormatter();
  const gridRef = useRef<AgGridReact<StudySample>>(null);

  const datasource = useMemo(
    () =>
      createAgGridDatasource<StudySample, PaginatedSamplesResponse, SampleSearchRequest>(
        studyQueryKeys.endpoints.samplesSearch(orgId, studyId),
        ({ filters, limit, skip, sortBy, sortOrder }) => {
          const validSortBy = sortBy && VALID_SORT_FIELDS.includes(sortBy) ? sortBy : 'created_at';

          return {
            skip,
            limit,
            sort_by: validSortBy as
              | 'name'
              | 'instrument_run_name'
              | 'created_at'
              | 'updated_at'
              | 'well_position'
              | 'demux_index'
              | 'study_name'
              | 'org_name',
            sort_order: (sortOrder ?? 'desc') as 'asc' | 'desc',
            filters: Object.keys(filters).length > 0 ? (filters as SampleSearchRequest['filters']) : null,
          };
        },
        {
          fetcher: request => searchStudySamples(orgId, studyId, request),
        },
      ),
    [orgId, studyId],
  );

  const {
    defaultColDef,
    handleFilterChanged,
    handleFirstDataRendered,
    handleGridReady,
    handleModelUpdated,
    handlePaginationChanged,
    pageSize,
  } = useSampleTableGrid<StudySample>({
    studyId,
    tableId: 'study-samples-table',
    validSortFields: VALID_SORT_FIELDS,
    paginationPageSizeKeyFn: paginationPageSizeKey,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    datasource,
    autoHeight: true,
  });

  const colDefs: ColDef<StudySample>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('columnHeaders.sampleName'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: true,
      },
      {
        field: 'sample_group',
        headerName: t('columnHeaders.sampleGroup'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: false,
      },
      {
        field: 'status',
        headerName: t('columnHeaders.status'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: false,
      },
      {
        field: 'run_qc',
        headerName: t('columnHeaders.runQc'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: false,
      },
      {
        field: 'image_qc',
        headerName: t('columnHeaders.imageQc'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: false,
      },
      {
        field: 'decode_qc',
        headerName: t('columnHeaders.decodeQc'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: false,
      },
      {
        field: 'instrument_run_name',
        headerName: t('columnHeaders.instrumentRun'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: true,
      },
      {
        field: 'assay_version',
        headerName: t('columnHeaders.assay'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: false,
      },
      {
        field: 'species',
        headerName: t('columnHeaders.species'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: false,
      },
      {
        field: 'created_at',
        headerName: t('columnHeaders.created'),
        filter: 'agDateColumnFilter',
        filterParams: DATE_FILTER_PARAMS,
        resizable: true,
        sortable: true,
        valueFormatter: ({ value }) => (value ? formatDate(value) : ''),
      },
      {
        // TODO: backend to add created_by_full_name to SampleWithNamesPublic
        colId: 'created_by',
        headerName: t('columnHeaders.createdBy'),
        resizable: true,
        sortable: false,
        filter: false,
        hide: true,
      },
    ],
    [formatDate, t],
  );

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource]);

  return (
    <Stack className={styles.tableContainer} fullWidth gap={4} orientation="vertical">
      <Stack alignItems="center" fullWidth justifyContent="space-between" orientation="horizontal">
        <Text as="h3">{t('study.studySamplesTitle')}</Text>
        <Stack gap={4} orientation="horizontal">
          <Text weight="bold">{t('study.studySamplesTotal')}:</Text>
          <Text>{totalSamples}</Text>
          <Text weight="bold">{t('study.studySamplesPassedQc')}:</Text>
          <Text>{passedQc}</Text>
        </Stack>
      </Stack>

      <Table
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        domLayout="autoHeight"
        enableAdvancedFilter={false}
        enableResponsiveResize={false}
        getRowId={(params: { data: StudySample }) => params.data.id}
        gridOptions={gridOptions}
        onFilterChanged={handleFilterChanged}
        onFirstDataRendered={handleFirstDataRendered}
        onGridPreDestroyed={e => flushColumnState(e.api, 'study-samples-table')}
        onGridReady={handleGridReady}
        onModelUpdated={handleModelUpdated}
        onPaginationChanged={handlePaginationChanged}
        pagination
        paginationPageSize={pageSize}
        paginationPageSizeSelector={PAGE_SIZE_OPTIONS}
        ref={gridRef}
        resizeDebounceMs={150}
        rowModelType="serverSide"
        tableId="study-samples-table"
      />
    </Stack>
  );
};
